import { klipyTagRegex } from "@inrev/common";
import { type FC, memo } from "react";
import { filterXSS } from "xss";
import { cn } from "../lib/utils";
import { GifDisplay } from "./gif/GifDisplay";

export type HtmlContentDisplayProps = {
	content?: string | null;
	truncate?: boolean;
	removeMargins?: boolean;
	hideGifs?: boolean;
};

type ContentSegment = { type: "html"; content: string } | { type: "klipy"; id: string };

export const HtmlContentDisplay: FC<HtmlContentDisplayProps> = memo(
	({ content, truncate, removeMargins, hideGifs }) => {
		if (!content || content.length === 0 || content === "" || content === "<p></p>") {
			return null;
		}

		const segments: ContentSegment[] = [];
		const klipyRegex = klipyTagRegex;
		let lastIndex = 0;
		let match: RegExpExecArray | null;

		while ((match = klipyRegex.exec(content)) !== null) {
			// Add HTML content before the klipy tag
			if (match.index > lastIndex) {
				const htmlContent = content.substring(lastIndex, match.index);
				segments.push({ type: "html", content: filterXSS(htmlContent) });
			}

			if (!hideGifs) {
				segments.push({ type: "klipy", id: match[1] });
			} else {
				segments.push({ type: "html", content: "<p>Gif Content</p>" });
			}
			lastIndex = match.index + match[0].length;
		}

		// Add remaining HTML content after the last klipy tag
		if (lastIndex < content.length) {
			const htmlContent = content.substring(lastIndex);
			segments.push({ type: "html", content: filterXSS(htmlContent) });
		}

		if (segments.length === 0) {
			return null;
		}

		return (
			<div
				data-testid="html-content-display"
				className={cn(
					"text-gray-800 text-[14px] font-[375] cursor-default object-contain",
					truncate ? "max-h-5 truncate [&>*]:truncate" : "",
					removeMargins ? "[&>*]:m-0" : "",
				)}
			>
				{segments.map((segment, index) => {
					if (segment.type === "klipy") {
						return <GifDisplay key={`klipy-${segment.id}-${index}`} id={segment.id} />;
					}
					return (
						<span key={`html-${index}`} dangerouslySetInnerHTML={{ __html: segment.content }} />
					);
				})}
			</div>
		);
	},
);
